import React from "react";
import { Box, Button, Chip, Divider, Grid, Typography } from "@mui/material";
import {
    BadgeOutlined,
    CalendarTodayOutlined,
    DescriptionOutlined,
    Download,
    PersonOutlined,
} from "@mui/icons-material";
import InformationTile from "components/Shared/InformationTile";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import { localizeDateTimeShort } from "utils/dateFormats";
import { useVendorQuotationDetailContext } from "contexts/VendorQuotationContext";

const VendorQuotationBasicDetail = () => {
    const { vendorQuotationDetail, loadingVendorQuotation } =
        useVendorQuotationDetailContext();

    if (loadingVendorQuotation) return <InfoGridSkeleton />;

    const d = vendorQuotationDetail;

    const handleDownload = async () => {
        try {
            const filename = `${d.vendorQuotationId} - ${d.title}.pdf`;
            const proxyUrl = `/api/proxy/download?url=${encodeURIComponent(d.pdfAttachment.url)}&filename=${encodeURIComponent(filename)}`;

            const response = await fetch(proxyUrl);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error("Download failed:", err);
        }
    };

    return (
        <Box padding={3}>
            <Grid container spacing={4}>

                {/* ── Key info tiles ─────────────────────────────────────────── */}
                <Grid item xs={6} md={3}>
                    <InformationTile
                        icon={() => <BadgeOutlined color="primary" />}
                        title="Quotation ID"
                        subtitle={d?.vendorQuotationId || "N/A"}
                    />
                </Grid>
                <Grid item xs={6} md={3}>
                    <InformationTile
                        icon={() => <PersonOutlined color="primary" />}
                        title="Created By"
                        subtitle={d?.createdBy?.name || "N/A"}
                    />
                </Grid>
                <Grid item xs={6} md={3}>
                    <InformationTile
                        icon={() => <CalendarTodayOutlined color="primary" />}
                        title="Created At"
                        subtitle={d?.createdAt ? localizeDateTimeShort(d.createdAt) : "N/A"}
                    />
                </Grid>
                <Grid item xs={6} md={3}>
                    <InformationTile
                        icon={() => <DescriptionOutlined color="primary" />}
                        title="Title"
                        subtitle={d?.title || "N/A"}
                    />
                </Grid>

                {/* ── PDF Attachment ──────────────────────────────────────────── */}
                {d?.pdfAttachment?.url && (
                    <Grid item xs={12}>
                        <Divider sx={{ mb: 2 }} />

                        {/* Header row */}
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                            <Typography variant="subtitle2" fontWeight={600} color="primary">
                                Quotation PDF
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<Download />}
                                onClick={handleDownload}
                                size="small"
                            >
                                Download PDF
                            </Button>
                        </Box>

                        {/* Embedded PDF viewer */}
                        <Box
                            component="iframe"
                            src={d.pdfAttachment.url}
                            title="Quotation PDF"
                            sx={{
                                width: "100%",
                                height: "80vh",
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                            }}
                        />
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default VendorQuotationBasicDetail;